import React, { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

import { FlexRow } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { ITask, ITaskWithNotes } from '../../tasks/interfaces/ITask';
import styles from '../styles.module.scss';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';

export const SimpleUploadTaskWithNotesDetail = ({
  taskTitle,
  elementLocator,
  task,
  uploadHandler,
  documentType,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
  children,
}: {
  taskTitle: string;
  elementLocator: string;
  task: ITaskWithNotes;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  documentType: DocumentType;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  children?: ReactNode;
}) => {
  const data = task.taskData;
  const evidence = task.evidence;
  const evidenceReasons: string[] = (data?.reason || []).map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  return (
    <Box data-label={elementLocator}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
        mb={0.75}
        id={task.id.toString()}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={0.5}>
            <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
          </Box>
          <Typography variant="h2">{taskTitle}</Typography>
        </Box>
        <Box>
          <Typography color="textSecondary" variant="subtitle2">
            {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
          </Typography>
        </Box>
      </Box>

      {children}

      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {evidenceExists(evidence) && evidence && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}

      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED && (
          <FileUpload
            leftItem={evidenceExists(evidence) ? '' : Resource.FILES}
            selectedFiles={onFileSelection}
            type={documentType}
            ongoingUpload={task.ongoingUpload?.[documentType]}
            uploadType="application/pdf,image/jpeg,image/png,image/jpg"
          />
        )}
      {task.state !== TaskState.REQUESTED && (
        <TaskNote taskID={task.id} originalNote={data?.note} />
      )}
      {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {data?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={data.reasonDescription}
            />
          )}
        </>
      )}
    </Box>
  );
};
